import { createRoot } from 'react-dom/client';
import './index.css';
import * as Sentry from "@sentry/react";

import { Amplify } from 'aws-amplify';
import awsExports from './aws-exports';
import { CompanyProvider } from './contexts/CompanyContext';
import { fetchAuthSession } from 'aws-amplify/auth';

// Initialize Sentry
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnv = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE || (typeof __SENTRY_RELEASE__ !== 'undefined' ? __SENTRY_RELEASE__ : undefined);
const isDevelopment = sentryEnv === 'development';

console.log('Sentry DSN:', sentryDsn ? 'configured' : 'missing');
console.log('Sentry Environment:', sentryEnv);
console.log('Sentry Release:', sentryRelease || 'not set');

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnv,
    release: sentryRelease,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.consoleLoggingIntegration({
        levels: ["log", "warn", "error"]
      }),
      Sentry.feedbackIntegration({
        colorScheme: "system",
        autoInject: false,
        showBranding: false,
        triggerLabel: "Report an Issue",
        formTitle: "Report an Issue",
        submitButtonLabel: "Submit",
        messagePlaceholder: "Describe the issue you're experiencing...",
        isNameRequired: false,
        isEmailRequired: true,
        successMessageText: "Thank you for your feedback!",
        useSentryUser: {
          email: 'email',
          name: 'username',
        },
        themeDark: {
          background: '#1a1a1a',
          backgroundHover: '#262626',
          foreground: '#e5e5e5',
          border: '#404040',
          accentBackground: '#FFC107',
          accentForeground: '#000000',
          submitBackground: '#FFC107',
          submitBackgroundHover: '#FFB300',
          submitForeground: '#000000',
        },
        themeLight: {
          background: '#ffffff',
          backgroundHover: '#f5f5f5',
          foreground: '#171717',
          border: '#e5e5e5',
          accentBackground: '#FFC107',
          accentForeground: '#000000',
          submitBackground: '#FFC107',
          submitBackgroundHover: '#FFB300',
          submitForeground: '#000000',
        }
      })
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.owlthat\.com/],
    replaysSessionSampleRate: isDevelopment ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true
  });
  console.log('Sentry initialized successfully');
  console.log('Session Replay sample rate:', isDevelopment ? '100%' : '10%');
  // Expose Sentry globally for testing
  (window as any).Sentry = Sentry;
} else {
  console.warn('Sentry DSN not configured');
}

// Configure Amplify BEFORE importing modules that call generateClient()
Amplify.configure(awsExports);

// Set Sentry user context from Cognito
async function setSentryUserContext() {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.payload;

    if (idToken) {
      Sentry.setUser({
        id: idToken.sub as string,
        email: idToken.email as string,
        username: idToken.name as string || idToken.email as string,
      });
      console.log('Sentry user context set from Cognito');
    }
  } catch (error) {
    console.log('No authenticated user for Sentry context');
  }
}

// Set user context immediately
setSentryUserContext();

// Dynamically import App after Amplify is configured to avoid race conditions
(async () => {
  const { default: App } = await import('./App.tsx');
  createRoot(document.getElementById('root')!).render(
    <CompanyProvider>
      <App />
    </CompanyProvider>
  );
})();
